import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@evocha/api-client";
import { ChevronDown, ChevronUp, Loader2, UserPlus } from "lucide-react";
import { dealerApi } from "../lib/api";
import { shortDate, tzs } from "../lib/format";
import { PageHead } from "../components/PageHead";
import { AgentPill, KycBadge, OrderPill } from "../components/Status";

interface AgentRow {
  id: string;
  code: string;
  business_name: string;
  phone: string;
  region: string | null;
  district: string | null;
  status: string;
  kyc_status: string;
  total_sales_minor: number;
  created_at: string | null;
}

interface AgentDetail {
  agent: AgentRow;
  wallet: { available_minor: number; reserved_minor: number; commission_minor: number } | null;
  recent_orders: {
    id: string;
    msisdn: string | null;
    mno_code: string;
    amount_minor: number;
    status: string;
    created_at: string | null;
  }[];
}

function errText(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

export function Agents() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showRecruit, setShowRecruit] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [pin, setPin] = useState("");
  const [recruitError, setRecruitError] = useState<string | null>(null);
  const [recruitOk, setRecruitOk] = useState<string | null>(null);

  const agents = useQuery({
    queryKey: ["dealer-agents"],
    queryFn: () =>
      dealerApi.get<{ data: AgentRow[]; total: number }>("/superdealer/v1/agents"),
  });
  const detail = useQuery({
    queryKey: ["dealer-agent", selected],
    enabled: Boolean(selected),
    queryFn: () => dealerApi.get<AgentDetail>(`/superdealer/v1/agents/${selected}`),
  });

  const recruit = useMutation({
    mutationFn: async (): Promise<{ agent: AgentRow; created: boolean; message?: string }> =>
      dealerApi.post("/superdealer/v1/agents", {
        business_name: businessName.trim(),
        phone: phone.replace(/\s/g, ""),
        region: region.trim() || null,
        district: district.trim() || null,
        pin,
      }),
    onSuccess: (res: { agent: AgentRow; created: boolean; message?: string }) => {
      setRecruitOk(
        res.created
          ? `${res.agent.business_name} (${res.agent.code}) joined your network. Share their login details privately.`
          : res.message ?? "This agent is already in your network.",
      );
      setRecruitError(null);
      setBusinessName("");
      setPhone("");
      setRegion("");
      setDistrict("");
      setPin("");
      void queryClient.invalidateQueries({ queryKey: ["dealer-agents"] });
    },
    onError: (err) => {
      setRecruitError(errText(err));
      setRecruitOk(null);
    },
  });

  const submitRecruit = (e: React.FormEvent) => {
    e.preventDefault();
    setRecruitError(null);
    setRecruitOk(null);
    if (businessName.trim().length < 2) return setRecruitError("Enter the agent's business name.");
    if (!/^\d{9,15}$/.test(phone.replace(/\s/g, ""))) return setRecruitError("Enter a valid phone number.");
    if (!/^\d{4,6}$/.test(pin)) return setRecruitError("The starting PIN is 4 to 6 digits.");
    recruit.mutate();
    return undefined;
  };

  return (
    <div>
      <PageHead
        title="My agents"
        description="Everyone selling under your Dealership code."
        actions={
          <button className="ev-btn ev-btn-primary ev-btn-sm" onClick={() => setShowRecruit((p) => !p)}>
            <UserPlus size={15} style={{ marginRight: 6 }} />
            {showRecruit ? "Close form" : "Recruit agent"}
          </button>
        }
      />

      {showRecruit && (
        <div className="admin-card" style={{ marginBottom: 16 }}>
          <div className="admin-card-header">
            <h3>Recruit a new agent</h3>
          </div>
          <form onSubmit={submitRecruit} className="dealer-form-grid">
            <label className="admin-field">
              <span>Business name</span>
              <input
                className="ev-input"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Kariakoo Airtime Mart"
                disabled={recruit.isPending}
              />
            </label>
            <label className="admin-field">
              <span>Phone number</span>
              <input
                className="ev-input"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0754 000 000"
                disabled={recruit.isPending}
              />
            </label>
            <label className="admin-field">
              <span>Region</span>
              <input
                className="ev-input"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="Dar es Salaam"
                disabled={recruit.isPending}
              />
            </label>
            <label className="admin-field">
              <span>District</span>
              <input
                className="ev-input"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                placeholder="Ilala"
                disabled={recruit.isPending}
              />
            </label>
            <label className="admin-field">
              <span>Starting PIN</span>
              <input
                className="ev-input"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="4–6 digits"
                disabled={recruit.isPending}
              />
            </label>
            <div className="admin-field">
              <span>&nbsp;</span>
              <button className="ev-btn ev-btn-primary" type="submit" disabled={recruit.isPending}>
                {recruit.isPending ? <Loader2 size={16} className="spin" /> : <UserPlus size={16} />}
                Add agent
              </button>
            </div>
          </form>
          {recruitError && <div className="dealer-alert dealer-alert-error">{recruitError}</div>}
          {recruitOk && <div className="dealer-alert dealer-alert-ok">{recruitOk}</div>}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Network ({agents.data?.total ?? 0})</h3>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-table-th">Agent</th>
                <th className="admin-table-th">Phone</th>
                <th className="admin-table-th">Status</th>
                <th className="admin-table-th">KYC</th>
                <th className="admin-table-th">Lifetime sales</th>
                <th className="admin-table-th">Joined</th>
                <th className="admin-table-th" aria-label="Expand" />
              </tr>
            </thead>
            <tbody>
              {(agents.data?.data ?? []).map((a) => (
                <tr
                  key={a.id}
                  className={`admin-table-row-hover${selected === a.id ? " dealer-row-selected" : ""}`}
                  onClick={() => setSelected(selected === a.id ? null : a.id)}
                >
                  <td className="admin-table-td">
                    <div className="admin-cell-stack">
                      <strong>{a.business_name}</strong>
                      <span className="ev-body-micro muted">{a.code}</span>
                    </div>
                  </td>
                  <td className="admin-table-td">{a.phone}</td>
                  <td className="admin-table-td">
                    <AgentPill status={a.status} />
                  </td>
                  <td className="admin-table-td">
                    <KycBadge status={a.kyc_status} />
                  </td>
                  <td className="admin-table-td">{tzs(a.total_sales_minor)}</td>
                  <td className="admin-table-td ev-body-micro muted">{shortDate(a.created_at)}</td>
                  <td className="admin-table-td">
                    {selected === a.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </td>
                </tr>
              ))}
              {!agents.isLoading && !agents.data?.data.length && (
                <tr>
                  <td className="admin-table-td" colSpan={7}>
                    <div className="dash-table-empty">
                      No agents yet. Recruit your first agent to start distributing float.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="admin-card" style={{ marginTop: 16 }}>
          <div className="admin-card-header">
            <h3>{detail.data?.agent.business_name ?? "Agent"} — 360° view</h3>
          </div>
          {detail.isLoading ? (
            <div className="dash-table-empty">
              <Loader2 size={16} className="spin" /> Loading…
            </div>
          ) : detail.data ? (
            <>
              <div className="admin-detail-grid">
                <div className="admin-stat-row">
                  <span className="ev-body-muted">Wallet balance</span>
                  <strong>{tzs(detail.data.wallet?.available_minor ?? 0)}</strong>
                </div>
                <div className="admin-stat-row">
                  <span className="ev-body-muted">Commission bucket</span>
                  <strong>{tzs(detail.data.wallet?.commission_minor ?? 0)}</strong>
                </div>
                <div className="admin-stat-row">
                  <span className="ev-body-muted">Status</span>
                  <AgentPill status={detail.data.agent.status} />
                </div>
                <div className="admin-stat-row">
                  <span className="ev-body-muted">KYC</span>
                  <KycBadge status={detail.data.agent.kyc_status} />
                </div>
              </div>
              <div className="dash-table-wrap" style={{ marginTop: 12 }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Recipient</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.recent_orders.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <span className="dash-mono">{o.id}</span>
                        </td>
                        <td>{o.msisdn ?? "—"}</td>
                        <td>{tzs(o.amount_minor)}</td>
                        <td>
                          <OrderPill status={o.status} />
                        </td>
                      </tr>
                    ))}
                    {!detail.data.recent_orders.length && (
                      <tr>
                        <td colSpan={4}>
                          <div className="dash-table-empty">No recent orders.</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="dash-table-empty">{errText(detail.error)}</div>
          )}
        </div>
      )}
    </div>
  );
}
